import type { IdentityFormData, FormValidation } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";
import { isAddressValid } from "@/screen/Identities/IdentityForm/identityAddressValidation";

function hasValue(value: string | null | undefined): boolean
{
    return typeof value === "string" && value.trim() !== "";
}

function hasNumericValue(value: number | null | undefined): boolean
{
    return value !== null && value !== undefined;
}

export function isEditFormValid(formData: IdentityFormData, validation: FormValidation): boolean
{
    // Un menor no tiene datos de contacto propios: el alta los deja en blanco a propósito (el contacto
    // es su tutor). Exigirlos aquí dejaba su edición sin salida, porque llegan vacíos de BD y el
    // formulario no los pide. Mismo criterio que el alta (isFormValid) y que las reglas de guardado de
    // borrador del backend, que tampoco los reclaman (ValidateAllForDraftSave).
    const editContactValidation = formData.isMinor ?
        true :
        (hasValue(formData.email) &&
        hasValue(formData.phone) &&
        hasValue(formData.countryPrefix));

    // Los nombres, además de no estar vacíos, deben cumplir el formato si se han tocado: `validation` llega a
    // true con lo cargado (un nombre legacy no bloquea) y se recalcula con PersonNameRules al escribir.
    const editBasicValidation =
        hasValue(formData.firstName) && validation.firstName &&
        hasValue(formData.lastName) && validation.lastName &&
        hasValue(formData.secondLastName) && validation.secondLastName &&
        editContactValidation &&
        // Las fechas se apoyan en `validation`, que ya incorpora las reglas de negocio
        // (nacimiento no futuro, validez no caducada) igual que en el alta.
        validation.birthday &&
        hasValue(formData.birthdayCountry) &&
        hasValue(formData.nationalCountry) &&
        hasValue(formData.idDocument) &&
        validation.idExpiration &&
        isAddressValid(formData.postalAddress);

    const editPhoneValidation = formData.isMinor || formData.useSinglePhone ?
        true :
        (hasValue(formData.operationPhone) &&
        hasValue(formData.signaturePhone) &&
        hasValue(formData.operationCountryPrefix) &&
        hasValue(formData.signatureCountryPrefix));

    const editFiscalAddressValidation = formData.sameAddress ? true : isAddressValid(formData.fiscalAddress);

    const editFinancialValidation = formData.isMinor ?
        validation.proofOfMinorGuardian :
        (hasValue(formData.economicActivity) &&
        validation.proofOfIncome &&
        validation.gafiQuestion &&
        validation.publicPosition);

    const editResidenceDocOk = formData.residenceCountry === "ES" ?
        hasNumericValue(formData.residenceOfficialDocument) :
        (hasValue(formData.residenceCountry) && hasNumericValue(formData.noResidenceDocument));

    const editFiscalDataValidation = formData.isMinor ? true :
        (hasNumericValue(formData.maritalStatus) &&
        hasValue(formData.residenceCountry) &&
        hasNumericValue(formData.fatcaStatus) &&
        editResidenceDocOk &&
        formData.hasTaxIdInUS !== undefined && formData.hasTaxIdInUS !== null &&
        formData.hasResidenceInUS !== undefined && formData.hasResidenceInUS !== null &&
        formData.hasTaxObligationInUS !== undefined && formData.hasTaxObligationInUS !== null);

    return !!(editBasicValidation && editPhoneValidation && editFiscalAddressValidation && editFinancialValidation && editFiscalDataValidation && (formData.idType === "DNI" || formData.idType === "NIE"));
}